import { endOfDay, format } from 'date-fns';

import { NeedToControlled } from '@modules/students/constants';
import { StudentOnlineApplicationEntity } from '@modules/students/entities';
import {
    StudentOnlineApplicationStatus,
    StudentOnlineApplicationStep,
} from '@modules/types/graphql';

import type { StudentOnlineApplicationFill } from '@modules/types/graphql';

export const STEPS_ORDER = [
    StudentOnlineApplicationStep.summary,
    StudentOnlineApplicationStep.medical,
    StudentOnlineApplicationStep.specials,
    StudentOnlineApplicationStep.documents,
];

export type UpdateStudentOnlineApplicationIntroductionStep = {
    passed: boolean;
};

export type UpdateStudentOnlineApplicationSummaryresponsible = {
    fullName: string;
    birthdate: Date | null;
    passportSerial: string;
    passportNumber: string;
    passportIssued: string;
    passportAddress: string;
    address: string;
    phone: string;
    email: string;
    isResponsibleChecked: boolean;
};

export type UpdateStudentOnlineApplicationSummaryMain = {
    fullName: string;
    fullNameGenitive: string;
    birthdate: Date | null;
    passportAddress: string;
    address: string;
    passportSerial: string;
    passportNumber: string;
    passportIssued: string;
    schoolName: string;
    studyYear: string;
    phone: string;
    email: string;
    vk: string;
    telegram: string;
};

export type UpdateStudentOnlineApplicationSummaryCriticalContact = {
    name: string;
    birthdate: Date | null;
    passportSerial: string;
    passportNumber: string;
    passportIssued: string;
    passportAddress: string;
    address: string;
    phone: string;
    email: string;
};

export type UpdateStudentOnlineApplicationSummaryRecreation = {
    selfLeft: radioBoolean;
    count: string;
    fromAge: string;
    assesment: string;
    mood: string;
};

export type UpdateStudentOnlineApplicationSummaryStep = {
    responsible: UpdateStudentOnlineApplicationSummaryresponsible;
    main: UpdateStudentOnlineApplicationSummaryMain;
    criticalContacts: UpdateStudentOnlineApplicationSummaryCriticalContact[];
    recreation: UpdateStudentOnlineApplicationSummaryRecreation;
};

type radioBoolean = '0' | '1';

export type UpdateStudentOnlineApplicationMedicalStep = {
    diets: string;
    proneToColdBool: radioBoolean;
    proneToCold: string;
    fractures: string;
    headInjuries: string;
    allergicDiseases: string;
    allergyMedications: string;
    chronicDiseases: string;
    medications: string;
    heatTransfer: string;
    insectBites: string;
    healthSpecials: string;
    fatigueBool: radioBoolean;
    fatigue: string;
    motionSicknessBool: radioBoolean;
    motionSickness: string;
    wearingGlassesBool: radioBoolean;
    wearingGlasses: string;
    fearDarkBool: radioBoolean;
    fearDark: string;
    otherSpecials: string;
};

export type UpdateStudentOnlineApplicationIndividualStep = {
    wantToServe: string;
    sports: string;
    pleasureActivities: string;
    specialSkills: string;
    unPleasureActivities: string;
    stressfulSituations: string;
    attitudeThings: string;
    selfCare: string;
    needToControlled: string[];
    needToControlledOther: string;
    preferCommunicate: string;
    communationDifficulties: string;
    characterTraits: string;
    badHabits: string;
};

export type UpdateStudentOnlineApplicationFormValues = {
    originalStudentOnlineApplication: StudentOnlineApplicationEntity;
    isAdult: boolean;
    isFutureAdult: boolean;

    // Шаг сохраняем как следующий после нажатия на любое действия в футере анкеты (сохранить/назад)
    // чтобы сохранить поведение загрузки сохранения данных на UI вводим временный параметр,
    // который не влияет на UI, но используется для запроса
    temporaryStep: StudentOnlineApplicationStep;

    currentStep: StudentOnlineApplicationStep;
    currentStatus: StudentOnlineApplicationStatus;

    introduction: UpdateStudentOnlineApplicationIntroductionStep;
    summary: UpdateStudentOnlineApplicationSummaryStep;
    medical: UpdateStudentOnlineApplicationMedicalStep;
    individual: UpdateStudentOnlineApplicationIndividualStep;
};

export class UpdateStudentOnlineApplicationRequest {
    step: StudentOnlineApplicationFill['step'];
    responsible: StudentOnlineApplicationFill['responsible'];
    main: StudentOnlineApplicationFill['main'];
    criticalContacts: StudentOnlineApplicationFill['criticalContacts'];
    recreation: StudentOnlineApplicationFill['recreation'];
    medical: StudentOnlineApplicationFill['medical'];
    individual: StudentOnlineApplicationFill['individual'];

    constructor(values: UpdateStudentOnlineApplicationFormValues) {
        const { temporaryStep, summary, medical, individual } = values;

        this.step = temporaryStep;

        this.responsible = {
            ...summary.responsible,
            birthdate: summary.responsible.birthdate
                ? format(endOfDay(summary.responsible.birthdate), 'yyyy-MM-dd')
                : '',
        };

        this.main = {
            fullNameGenitive: summary.main.fullNameGenitive,
            passportSerial: summary.main.passportSerial,
            passportNumber: summary.main.passportNumber,
            passportAddress: summary.main.passportAddress,
            passportIssued: summary.main.passportIssued,
            address: summary.main.address,
            phone: summary.main.phone,
            vk: summary.main.vk,
            telegram: summary.main.telegram,
        };

        this.criticalContacts = summary.criticalContacts.map(criticalContact => ({
            ...criticalContact,
            birthdate: criticalContact.birthdate
                ? format(endOfDay(criticalContact.birthdate), 'yyyy-MM-dd')
                : '',
        }));

        this.recreation = {
            selfLeft: summary.recreation.selfLeft,
            assesment: summary.recreation.assesment,
            count: summary.recreation.count,
            fromAge: summary.recreation.fromAge,
            mood: summary.recreation.mood,
        };

        this.medical = {
            diets: medical.diets,
            proneToColdBool: medical.proneToColdBool,
            proneToCold: medical.proneToCold,
            fractures: medical.fractures,
            headInjuries: medical.headInjuries,
            allergicDiseases: medical.allergicDiseases,
            allergyMedications: medical.allergyMedications,
            chronicDiseases: medical.chronicDiseases,
            medications: medical.medications,
            heatTransfer: medical.heatTransfer,
            insectBites: medical.insectBites,
            healthSpecials: medical.healthSpecials,
            fatigueBool: medical.fatigueBool,
            fatigue: medical.fatigue,
            motionSicknessBool: medical.motionSicknessBool,
            motionSickness: medical.motionSickness,
            wearingGlassesBool: medical.wearingGlassesBool,
            wearingGlasses: medical.wearingGlasses,
            fearDarkBool: medical.fearDarkBool,
            fearDark: medical.fearDark,
            otherSpecials: medical.otherSpecials,
        };

        this.individual = {
            wantToServe: individual.wantToServe,
            sports: individual.sports,
            pleasureActivities: individual.pleasureActivities,
            specialSkills: individual.specialSkills,
            unPleasureActivities: individual.unPleasureActivities,
            stressfulSituations: individual.stressfulSituations,
            attitudeThings: individual.attitudeThings,
            selfCare: individual.selfCare,
            needToControlled: individual.needToControlled,
            needToControlledOther: individual.needToControlled.includes(NeedToControlled.OTHER)
                ? individual.needToControlledOther
                : '',
            preferCommunicate: individual.preferCommunicate,
            communationDifficulties: individual.communationDifficulties,
            characterTraits: individual.characterTraits,
            badHabits: individual.badHabits,
        };
    }

    setRadioBoolean(radioBoolean: string, value: string): string {
        return radioBoolean === '1' ? value : '';
    }
}
